import { Download, Printer, QrCode as QrCodeIcon, ShieldCheck } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { useApp } from "../state/AppContext";
import type { Student } from "../types";
import { Button, InlineNotice } from "./ui";

interface QrCardProps {
  student: Student;
  canDownload?: boolean;
  showActions?: boolean;
}

const escapeHtml = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#039;",
      })[character] ?? character,
  );

const loadImage = (source: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Imej QR tidak dapat dimuatkan."));
    image.src = source;
  });

const drawCentredText = (
  context: CanvasRenderingContext2D,
  value: string,
  y: number,
  maxWidth: number,
) => {
  if (context.measureText(value).width <= maxWidth) {
    context.fillText(value, 500, y);
    return;
  }

  const words = value.split(/\s+/);
  const lines: string[] = [];
  let current = "";
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (context.measureText(candidate).width > maxWidth && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });
  if (current) lines.push(current);
  lines.slice(0, 2).forEach((line, index) => context.fillText(line, 500, y + index * 46));
};

export function QrCard({
  student,
  canDownload = false,
  showActions = true,
}: QrCardProps) {
  const { settings } = useApp();
  const [dataUrl, setDataUrl] = useState("");
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let active = true;
    setDataUrl("");
    setError("");
    QRCode.toDataURL(student.qrPayload, {
      errorCorrectionLevel: "H",
      margin: 2,
      width: 900,
      color: {
        dark: "#17151c",
        light: "#ffffff",
      },
    })
      .then((url) => {
        if (active) setDataUrl(url);
      })
      .catch(() => {
        if (active) setError("Kod QR tidak dapat dijana pada peranti ini.");
      });

    return () => {
      active = false;
    };
  }, [student.qrPayload]);

  const downloadCard = async () => {
    if (!dataUrl || !canDownload) return;
    setDownloading(true);
    setError("");
    try {
      const qrImage = await loadImage(dataUrl);
      const canvas = document.createElement("canvas");
      canvas.width = 1000;
      canvas.height = 1320;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Canvas tidak disokong.");

      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = "#3b1678";
      context.fillRect(0, 0, canvas.width, 160);
      context.fillStyle = "#e8a912";
      context.fillRect(0, 160, canvas.width, 16);

      context.textAlign = "center";
      context.fillStyle = "#ffffff";
      context.font = "700 52px Arial, sans-serif";
      context.fillText(settings.schoolShortName, 500, 70);
      context.font = "500 28px Arial, sans-serif";
      context.fillText("Kad Kehadiran JosephCare", 500, 118);

      context.drawImage(qrImage, 120, 220, 760, 760);
      context.fillStyle = "#17151c";
      context.font = "700 42px Arial, sans-serif";
      drawCentredText(context, student.name, 1050, 820);
      context.fillStyle = "#5c5766";
      context.font = "600 30px Arial, sans-serif";
      context.fillText(`${student.className}  ·  ${student.code}`, 500, 1165);
      context.fillStyle = "#3b1678";
      context.font = "600 23px Arial, sans-serif";
      context.fillText("Imbas untuk merekod kehadiran", 500, 1238);

      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `kad-qr-${student.code.toLowerCase()}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setError("Kad QR tidak dapat dimuat turun. Sila cuba lagi.");
    } finally {
      setDownloading(false);
    }
  };

  const printCard = () => {
    if (!dataUrl || !canDownload) return;
    const printWindow = window.open("", "_blank", "width=720,height=860");
    if (!printWindow) {
      setError("Benarkan tetingkap timbul untuk mencetak kad QR.");
      return;
    }

    const safeName = escapeHtml(student.name);
    const safeClass = escapeHtml(student.className);
    const safeCode = escapeHtml(student.code);
    const safeSchool = escapeHtml(settings.schoolName);
    printWindow.document.write(`<!doctype html>
      <html lang="ms">
        <head>
          <meta charset="utf-8" />
          <title>Kad QR ${safeName}</title>
          <style>
            @page { size: A6 portrait; margin: 8mm; }
            * { box-sizing: border-box; }
            body { margin: 0; font-family: Arial, sans-serif; color: #17151c; }
            .card { min-height: 132mm; border: 2px solid #3b1678; border-radius: 12px; overflow: hidden; text-align: center; }
            header { padding: 14px 12px 12px; color: white; background: #3b1678; border-bottom: 5px solid #e8a912; }
            header strong, header span { display: block; }
            header strong { font-size: 18px; }
            header span { margin-top: 3px; font-size: 10px; opacity: .84; }
            img { width: 78mm; height: 78mm; display: block; margin: 8mm auto 3mm; }
            h1 { max-width: 92mm; margin: 0 auto 2mm; font-size: 16px; line-height: 1.25; }
            p { margin: 0; color: #5c5766; font-size: 11px; }
            footer { margin: 6mm 0 4mm; color: #3b1678; font-size: 10px; font-weight: 700; }
          </style>
        </head>
        <body>
          <article class="card">
            <header><strong>${safeSchool}</strong><span>Kad Kehadiran JosephCare</span></header>
            <img src="${dataUrl}" alt="Kod QR ${safeName}" />
            <h1>${safeName}</h1>
            <p>${safeClass} &nbsp;·&nbsp; ${safeCode}</p>
            <footer>Imbas untuk merekod kehadiran</footer>
          </article>
          <script>
            const image = document.querySelector("img");
            const ready = () => { window.print(); window.close(); };
            image.complete ? setTimeout(ready, 150) : image.addEventListener("load", ready);
          </script>
        </body>
      </html>`);
    printWindow.document.close();
  };

  return (
    <section className="qr-card" aria-label={`Kad QR ${student.name}`}>
      <div className="qr-card__masthead">
        <span className="qr-card__mark">
          <img src="/school-mark.svg" alt="" />
        </span>
        <span>
          <strong>{settings.schoolShortName}</strong>
          <small>Kad Kehadiran JosephCare</small>
        </span>
        <ShieldCheck size={20} aria-label="Kod disahkan" />
      </div>

      <div className="qr-card__code">
        {dataUrl ? (
          <img src={dataUrl} alt={`Kod QR unik untuk ${student.name}`} />
        ) : error ? (
          <span className="qr-card__placeholder qr-card__placeholder--error">
            <QrCodeIcon size={42} />
            Gagal menjana QR
          </span>
        ) : (
          <span className="qr-card__placeholder" aria-label="Sedang menjana kod QR">
            <span className="spinner" aria-hidden="true" />
            Menjana QR…
          </span>
        )}
      </div>

      <div className="qr-card__identity">
        <strong>{student.name}</strong>
        <span>{student.className}</span>
        <code>{student.code}</code>
      </div>

      {showActions &&
        (canDownload ? (
          <div className="qr-card__actions">
            <Button
              variant="secondary"
              size="compact"
              icon={Download}
              onClick={downloadCard}
              loading={downloading}
              disabled={!dataUrl}
            >
              Muat turun PNG
            </Button>
            <Button
              variant="secondary"
              size="compact"
              icon={Printer}
              onClick={printCard}
              disabled={!dataUrl}
            >
              Cetak
            </Button>
          </div>
        ) : (
          <InlineNotice tone="neutral">
            Muat turun dan cetakan QR dihadkan oleh pentadbir.
          </InlineNotice>
        ))}

      {error && dataUrl && (
        <p className="qr-card__error" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
